export type ESignType = 'wet' | 'image' | 'text';

/** Wire/draft shape of an eSign field answer. `data` is a media id (wet/image) or the typed text. */
export interface ESignValue {
  type: ESignType | '';
  data: string;
  font_type: string;
  timestamp: string;
}

export const EMPTY_ESIGN: ESignValue = Object.freeze({ type: '', data: '', font_type: '', timestamp: '' });

const ESIGN_TYPES = new Set<string>(['wet', 'image', 'text']);

export const isESignType = (value: unknown): value is ESignType => typeof value === 'string' && ESIGN_TYPES.has(value);

const asString = (value: unknown) => (typeof value === 'string' ? value : '');

/** Normalizes a stored/schema/draft value (object or JSON string) into an `ESignValue`. Never throws. */
export const parseESignValue = (value: unknown): ESignValue => {
  let raw = value;
  if (typeof raw === 'string') {
    if (!raw.trim()) return { ...EMPTY_ESIGN };
    try {
      raw = JSON.parse(raw);
    } catch {
      return { ...EMPTY_ESIGN };
    }
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { ...EMPTY_ESIGN };

  const record = raw as Record<string, unknown>;
  const data = asString(record.data);
  if (!isESignType(record.type) || !data.trim()) return { ...EMPTY_ESIGN };

  return {
    type: record.type,
    data,
    font_type: record.type === 'text' ? asString(record.font_type) : '',
    timestamp: asString(record.timestamp),
  };
};

export const isESignSigned = (value: ESignValue) => !!value.type && !!value.data.trim();

export const isImageESign = (value: ESignValue) => value.type === 'wet' || value.type === 'image';

/** Two values are the same signature when they'd render identically (timestamp is ignored). */
export const isSameSignature = (a: ESignValue, b: ESignValue) =>
  a.type === b.type && a.data === b.data && (a.type !== 'text' || a.font_type === b.font_type);
